import {
  Children,
  cloneElement,
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
  createRef,
  MutableRefObject,
  KeyboardEvent,
} from "react"

import styled from "styled-components"
import { v4 as uuid } from "uuid"

import {
  IconSimpleArrowRightMedium,
  IconSimpleArrowUpMedium,
  IconSimpleArrowDownMedium,
  IconSize,
} from "@izettle/otto"

import { InputSize, SemanticState, KeyCodes } from "../../../enumerations"
import { useIsMobile } from "../../../hooks/use-is-mobile"
import { usePrevious } from "../../../utils/use-previous"
import { FloatingLabel } from "../../floating-label"
import { InputIcon } from "../../input-icon"
import { IconMetadata } from "../../svg-icon"
import { SelectAction, SelectActionsWrapper } from "../select-action"
import { SelectList } from "../select-list"
import { SelectOptionLoader } from "../select-option-loader"
import { SelectOptionTitle } from "../select-option-title"
import { SelectOption, SelectOptionAll } from "../select-options"
import { SelectValues, SelectValue } from "../select-value"

import { InputBaseStyled, StyledImage, ImageContainer } from "./styles"
import { SelectProps, SelectValueType, Option } from "./types"

const StyledInputIcon = styled(InputIcon)`
  width: 16px;
  min-width: 16px;
`

/**
 * Legend:
 *
 *   Option item:
 *     Every visible option in the list of options.
 *     Not every one of them conceals a value, some are decorational
 *
 *   Option value:
 *     Option items that hold an actual value
 */

export const Select = ({
  id = uuid(),
  disabled,
  children,
  className,
  label,
  svgIcon,
  customIcon,
  value,
  textSubmit,
  textSelectAll,
  textSelectAllDescription,
  textAllSelected,
  textInputButton,
  textRenderedValue,
  focusedItemIndex,
  isSelectAllOptionPresent = false,
  isMultiple = false,
  isRequired = false,
  isLoading = false,
  isExpanded = false,
  isLabelHidden = false,
  size = InputSize.Large,
  semanticState = SemanticState.Default,
  isErrorStateOverriden,

  // Overriden aria props
  ariaOwns,
  ariaLabelledBy,
  ariaDescribedBy,
  ariaInvalid,

  onChange,
}: SelectProps) => {
  /**
   * References
   */

  const refInputBase: MutableRefObject<HTMLDivElement | null> = useRef(null)
  const refList: MutableRefObject<HTMLDivElement | null> = useRef(null)

  /**
   * State flags
   */

  const [isExpandedState, setIsExpandedState] = useState(isExpanded || false)
  const prevIsExpandedState = usePrevious(isExpandedState)
  const [isElevated, setIsElevated] = useState(value !== undefined)
  const [isFocused, setIsFocused] = useState(false)
  const isMobile = useIsMobile()

  /**
   * Current, previous and available values, for diffing purposes
   */

  const previousValue = useRef<SelectValueType | undefined | symbol>(
    Symbol("previousValue"),
  )
  const previousOptionValuesHash = useRef<string | symbol>(
    Symbol("previousOptionValuesHash"),
  )
  const [currentValue, setCurrentValue] = useState<Option | Option[] | null>(
    isMultiple ? [] : null,
  )
  const [optionValues, setOptionValues] = useState<Option[]>([])

  /**
   * Available Focusable items & Focused items
   */

  const [focusedItemIndexState, setFocusedItemIndexState] = useState(-1)
  const [refFocusableItems, setRefFocusableItems] = useState<any>([])

  /**
   * Auxiliary precomputed states
   */

  const count = useMemo(() => {
    if (isMultiple) {
      return (currentValue && (currentValue as Option[]).length) || 0
    } else if (currentValue !== undefined) {
      return 1
    } else {
      return 0
    }
  }, [isMultiple, currentValue])

  const isDisabled = disabled || (optionValues.length === 0 && !isLoading)

  /**
   * Filtered children
   */

  const optionItems = Children.toArray(children).filter((child: any) => {
    if (!child) {
      return false
    }
    switch (child.type) {
      case SelectOption:
      case SelectOptionTitle:
      case SelectOptionLoader:
        return true
      default:
        return false
    }
  })

  const actionItems = Children.toArray(children).filter((child: any) => {
    if (!child) {
      return false
    }
    switch (child.type) {
      case SelectAction:
        return true
      default:
        return false
    }
  })

  const setIsExpanded = useCallback((status: boolean) => {
    setIsExpandedState((state) => (!status && isExpanded ? state : status))
  }, [])

  const setFocus = useCallback(
    (index) => {
      isExpandedState && refFocusableItems[index]?.current?.focus()
    },
    [isExpandedState, refFocusableItems],
  )

  /**
   * Navigation handlers
   */

  const handlePreviousItem = useCallback(() => {
    if (isExpandedState) {
      let previousSelectedOption =
        focusedItemIndexState <= 0
          ? refFocusableItems.length - 1
          : focusedItemIndexState - 1
      let iteration = 0
      while (
        optionValues[
          previousSelectedOption + (isSelectAllOptionPresent && isMultiple ? 1 : 0)
        ]?.disabled &&
        iteration < refFocusableItems.length
      ) {
        iteration += 1
        previousSelectedOption =
          previousSelectedOption <= 0
            ? refFocusableItems.length - 1
            : previousSelectedOption - 1
      }
      if (iteration < refFocusableItems.length) {
        setFocusedItemIndexState(previousSelectedOption)
      }
    }
  }, [
    isSelectAllOptionPresent,
    isMultiple,
    isExpandedState,
    focusedItemIndexState,
    optionValues,
  ])

  const handleNextItem = useCallback(() => {
    if (isExpandedState) {
      let nextSelectedOption =
        focusedItemIndexState < refFocusableItems.length - 1
          ? focusedItemIndexState + 1
          : 0
      let iteration = 0
      while (
        optionValues[
          nextSelectedOption + (isSelectAllOptionPresent && isMultiple ? 1 : 0)
        ]?.disabled &&
        iteration < refFocusableItems.length
      ) {
        iteration += 1
        nextSelectedOption =
          nextSelectedOption < refFocusableItems.length - 1 ? nextSelectedOption + 1 : 0
      }
      if (iteration < refFocusableItems.length) {
        setFocusedItemIndexState(nextSelectedOption)
      }
    }
  }, [
    isSelectAllOptionPresent,
    isMultiple,
    isExpandedState,
    focusedItemIndexState,
    optionValues,
  ])

  const handleKeyDown = (event: KeyboardEvent) => {
    switch (event.which) {
      case KeyCodes.Tab:
      case KeyCodes.Escape:
        setIsFocused(false)
        setIsExpanded(false)
        break
      case KeyCodes.Enter:
      case KeyCodes.Space:
        event.preventDefault()
        isMobile
          ? !isExpandedState && setIsExpanded(true)
          : setIsExpanded(!isExpandedState)
        break
      case KeyCodes.Down:
        event.preventDefault()
        handleNextItem()
        break
      case KeyCodes.Up:
        event.preventDefault()
        handlePreviousItem()
        break
      default:
        break
    }
  }

  const handleEscape = useCallback(() => {
    if (isExpandedState && refInputBase.current) {
      /* Remove focus from element and children on escape */
      refInputBase.current.focus()
      refInputBase.current.blur()
    }

    setIsExpanded(false)
    setIsFocused(false)
    setFocusedItemIndexState(-1)
  }, [isMobile, isExpandedState, currentValue, setIsExpanded])

  const handleClickOutside = useCallback(
    (event) => {
      if (
        (isMobile && isExpandedState) ||
        refInputBase.current?.contains(event.target) ||
        refList.current?.contains(event.target)
      ) {
        return
      }
      handleEscape()
    },
    [isMobile, isExpandedState, currentValue, handleEscape],
  )

  const handleTab = useCallback(() => {
    if (isExpandedState && focusedItemIndexState === -1) {
      setFocusedItemIndexState(0)
      refFocusableItems[0]?.current.focus()
    } else if (isExpandedState && focusedItemIndexState !== -1) {
      setFocusedItemIndexState(-1)
    }
  }, [isMobile, focusedItemIndexState, setIsExpanded])

  const handleClick = useCallback(() => {
    if (isDisabled) {
      return
    }
    setIsExpanded(!isExpandedState)
    setIsFocused(true)
  }, [isDisabled, isExpandedState, setIsExpanded])

  const getSemanticState = () => {
    return isRequired &&
      (currentValue === null || (isMultiple && (currentValue as Option[]).length === 0))
      ? SemanticState.Error
      : semanticState
  }

  const getAriaProps = () => ({
    ariaDescribedBy,
    ariaInvalid,
    ariaOwns:
      ariaOwns !== undefined
        ? ariaOwns
        : `${
            (isMultiple && isSelectAllOptionPresent && `${id}-option-select-all`) || ""
          }${optionValues
            .map((_option: Option, index: number) => `${id}-option-${index}`)
            .join(" ")}`,
    ariaRequired: isRequired,
    ariaMultiselectable: isMultiple,
    ariaBusy: isLoading,
    ariaExpanded: isExpandedState,
    ariaLabelledBy:
      ariaLabelledBy !== undefined
        ? ariaLabelledBy
        : (isMultiple && Array.isArray(currentValue) && currentValue.length !== 0) ||
          (!isMultiple && currentValue !== null && `${id}-label ${id}-value`) ||
          `${id}-label`,
  })

  /**
   * Side effects
   */

  // Focus on the first value upon expansion
  useEffect(() => {
    setFocusedItemIndexState(-1)
    if (!prevIsExpandedState && isExpandedState) {
      setFocusedItemIndexState(0)
    }
    //  else if (prevIsExpandedState && !isExpandedState) {
    //   refInputBase.current?.focus()
    // }
  }, [prevIsExpandedState, isExpandedState])

  // Set isExpanded status if prop updates

  useEffect(() => {
    if (isExpanded) {
      setIsExpanded(true)
    }
  }, [isExpanded])

  // Focus on the focused option upon navigational change

  useEffect(() => {
    setFocus(focusedItemIndexState)
  }, [focusedItemIndexState])

  // Focus on the focused option received from props

  useEffect(() => {
    setFocus(focusedItemIndex)
  }, [focusedItemIndex])

  // Bind the mousedown event handler

  useEffect(() => {
    window.document.addEventListener("mousedown", handleClickOutside, false)
    return () => {
      window.document.removeEventListener("mousedown", handleClickOutside, false)
    }
  }, [isMobile, isExpandedState, currentValue, isExpanded])

  // Handle state when the value is changed externally via the prop

  useEffect(() => {
    const optionValuesHash = optionValues.map((option: Option) => option.value).join("")
    if (isMultiple) {
      if (
        previousValue.current !== ((value || []) as []).join("") ||
        optionValuesHash !== previousOptionValuesHash.current
      ) {
        if (
          (typeof previousOptionValuesHash.current !== "symbol" &&
            optionValues.length === 0) ||
          value === undefined ||
          value === null ||
          (Array.isArray(value) && value.length === 0)
        ) {
          previousValue.current = ((value as []) || []).join("")
          setIsExpanded(false)
          setCurrentValue([])
          setIsElevated(false)
          onChange?.([])
        } else if (typeof previousOptionValuesHash.current !== "symbol") {
          const nextValue = optionValues.filter(
            (a: any) => (value as []).find((b: any) => b === a.value) !== undefined,
          )
          if (nextValue.length === 0) {
            setIsExpanded(false)
            setCurrentValue([])
            setIsElevated(false)
            previousValue.current = ""
            onChange?.([])
          } else {
            setCurrentValue(nextValue)
            setIsElevated(true)
            previousValue.current = (value as []).join("")
          }
        } else {
          previousValue.current = (value as []).join("")
        }

        // Update previous values
        previousOptionValuesHash.current = optionValuesHash
      }
    } else if (
      previousValue.current !== value ||
      optionValuesHash !== previousOptionValuesHash.current
    ) {
      if (
        (typeof previousOptionValuesHash.current !== "symbol" &&
          optionValues.length === 0) ||
        value === undefined ||
        value === null
      ) {
        previousValue.current = value
        setIsExpanded(false)
        setCurrentValue(null)
        setIsElevated(false)
        onChange?.(null)
      } else if (typeof previousOptionValuesHash.current !== "symbol") {
        const nextValue = optionValues.find((a: any) => a.value === value) ?? null
        if (nextValue === null) {
          setIsExpanded(false)
          setCurrentValue(null)
          setIsElevated(false)
          previousValue.current = null
          onChange?.(null)
        } else {
          setCurrentValue(nextValue)
          setIsElevated(true)
          previousValue.current = value
        }
      } else {
        previousValue.current = value
      }

      // Update previous values
      previousOptionValuesHash.current = optionValuesHash
    }
  }, [value, optionValues])

  // Repopulate the available option values list upon the children mutation

  useEffect(() => {
    // Set option values
    const nextOptionValues = optionItems
      .map((child: any) => {
        switch (child.type) {
          case SelectOption:
            return {
              value: child.props.value,
              text: child.props.text,
              ...((child.props.image && { image: child.props.image }) || {}),
              disabled: child.props.disabled,
            } as Option
          default:
            return false
        }
      })
      .filter(Boolean) as Option[]
    setOptionValues(nextOptionValues)

    // Set focusable option item references

    const itemsCount =
      nextOptionValues.length + (isSelectAllOptionPresent && isMultiple ? 1 : 0)
    setRefFocusableItems(
      Array(itemsCount)
        .fill(true)
        .map(() => createRef()),
    )
  }, [isMultiple, isSelectAllOptionPresent, children])

  /**
   * Rendering
   */

  // Render the value shown in the select field

  const renderValue = () => {
    if (textRenderedValue) {
      return textRenderedValue
    }
    if (isMultiple && Array.isArray(currentValue)) {
      if (
        currentValue.length < optionValues.length ||
        (optionValues.length > 0 && !textAllSelected)
      ) {
        return currentValue.map((option: Option) => option.text).join(", ")
      }
      return (optionValues.length > 0 && textAllSelected) || null
    }
    return currentValue && (currentValue as Option).text
  }

  // Render the value along with the container based on conditions

  const renderValueContainer = () => {
    return count > 1 && count < optionValues.length ? (
      <SelectValues count={count} isDisabled={isDisabled} id={`${id}-value`}>
        {renderValue()}
      </SelectValues>
    ) : (
      <>
        {isMultiple && Array.isArray(currentValue)
          ? currentValue.length === 1 &&
            currentValue[0].image && (
              <ImageContainer>
                <StyledImage src={currentValue[0].image} />
              </ImageContainer>
            )
          : currentValue &&
            (currentValue as Option).image && (
              <ImageContainer>
                <StyledImage src={(currentValue as Option).image} />
              </ImageContainer>
            )}
        <SelectValue id={`${id}-value`}>{renderValue()}</SelectValue>
      </>
    )
  }

  // Render the options list

  const renderOptionItems = () => {
    // Loading state

    if (isLoading) {
      return <SelectOptionLoader />
    }

    // Construct children

    const constructedOptionItems = [
      ...(isMultiple && isSelectAllOptionPresent
        ? [
            <SelectOptionAll
              key="select-option-all"
              id={`${id}-option-select-all`}
              text={textSelectAll as string}
              isMixed={(currentValue as Option[]).length !== 0}
              isExpanded={isExpandedState}
              description={textSelectAllDescription as string}
              isSelected={(currentValue as Option[]).length === optionValues.length}
              onSelect={() => {
                setIsExpanded(false)
                refInputBase.current?.focus()
                if ((currentValue as Option[]).length === optionValues.length) {
                  if (!isRequired) {
                    setIsElevated(false)
                    setCurrentValue([])
                    onChange?.([])
                  }
                } else {
                  const nextValue = JSON.parse(JSON.stringify(optionValues))
                  setCurrentValue(nextValue)
                  setIsElevated(nextValue !== undefined)
                  const valueArray = (nextValue as []).map(
                    (option: Option) => option.value,
                  )
                  onChange?.(valueArray)
                }
              }}
            />,
          ]
        : []),
      ...optionItems,
    ]

    // Construct props

    const { optionItemsProps } = constructedOptionItems.reduce(
      (accumulator: any, child: any, index) => {
        const commonProps = {
          key: accumulator.optionValueIndex,
          ref: refFocusableItems[accumulator.optionValueIndex],
          onOptionItemFocused: () =>
            setFocusedItemIndexState(accumulator.optionValueIndex),
          onPrevious: handlePreviousItem,
          onNext: handleNextItem,
          onEscape: handleEscape,
          onTab: handleTab,
        }

        switch (child.type) {
          case SelectOption: {
            const selectedValue = optionValues.find(
              (option: Option) => option.value === child.props.value,
            )
            if (!selectedValue) {
              return {
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                optionValueIndex: accumulator.optionValueIndex + 1,
                optionItemsProps: [
                  ...accumulator.optionItemsProps,
                  {
                    key: index,
                    id: `${id}-option-${index}`,
                  },
                ],
              }
            }

            const tabIndex = isDisabled ? -1 : 0
            let selectOptionProps = {}

            if (isMultiple) {
              const selectedValueIndex = Array.isArray(currentValue)
                ? currentValue.findIndex(
                    (option: Option) => option.value === selectedValue.value,
                  )
                : -1
              const isValueCurrentlySelected = selectedValueIndex > -1
              const isEveryValueSelected =
                (currentValue as Option[]).length === optionValues.length
              const onSelect = () => {
                let nextValue = []
                if (isSelectAllOptionPresent && isEveryValueSelected) {
                  nextValue = JSON.parse(JSON.stringify(currentValue))
                  nextValue.splice(selectedValueIndex, 1)
                } else if (
                  isSelectAllOptionPresent &&
                  isRequired &&
                  (currentValue as Option[]).length === 1 &&
                  isValueCurrentlySelected
                ) {
                  nextValue = JSON.parse(JSON.stringify(currentValue))
                } else {
                  nextValue = isValueCurrentlySelected
                    ? (currentValue as Option[]).filter(
                        (option: Option) => option.value !== selectedValue.value,
                      )
                    : [...(currentValue as Option[]), selectedValue]
                }
                const valueArray = (nextValue as []).map((option: Option) => option.value)
                previousValue.current = valueArray.join("")
                setCurrentValue(nextValue)
                setIsElevated(nextValue.length > 0)
                onChange?.(valueArray)
              }
              selectOptionProps = {
                onSelect,
                tabIndex,
                isMultiple,
                isExpanded: isExpandedState,
                isSelected: isValueCurrentlySelected,
              }
            } else {
              selectOptionProps = {
                tabIndex,
                isMobile,
                isExpanded: isExpandedState,
                isSelected:
                  currentValue && (currentValue as Option).value === selectedValue.value,
                onSelect: () => {
                  setCurrentValue(selectedValue)
                  setIsExpanded(false)
                  setIsElevated(selectedValue !== undefined)
                  refInputBase.current?.focus()
                  onChange?.(selectedValue?.value)
                },
              }
            }

            return {
              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
              optionValueIndex: accumulator.optionValueIndex + 1,
              optionItemsProps: [
                ...accumulator.optionItemsProps,
                {
                  ...commonProps,
                  ...selectOptionProps,
                  key: index,
                  id: `${id}-option-${index}`,
                },
              ],
            }
          }

          case SelectOptionAll:
            return {
              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
              optionValueIndex: accumulator.optionValueIndex + 1,
              optionItemsProps: [...accumulator.optionItemsProps, commonProps],
            }
          default:
            return {
              optionValueIndex: accumulator.optionValueIndex,
              optionItemsProps: [
                ...accumulator.optionItemsProps,
                {
                  key: `title-${index}`,
                },
              ],
            }
        }
      },
      {
        optionValueIndex: 0,
        optionItemsProps: [],
      },
    )

    // Inject children with props

    return constructedOptionItems.map((child: any, index: number) =>
      cloneElement(child, optionItemsProps[index]),
    )
  }

  // Conditionally render the desktop / mobile dropdown icon

  const renderInputIcon = () => {
    let nextSvgIcon: IconMetadata<IconSize.Medium>
    if (isMobile) {
      nextSvgIcon = IconSimpleArrowRightMedium
    } else if (isExpandedState) {
      nextSvgIcon = IconSimpleArrowUpMedium
    } else {
      nextSvgIcon = IconSimpleArrowDownMedium
    }
    return isMobile ? (
      <StyledInputIcon
        tabIndex={-1}
        svgIcon={nextSvgIcon}
        title={textInputButton}
        isDisabled={isDisabled}
      />
    ) : (
      <InputIcon
        tabIndex={-1}
        svgIcon={nextSvgIcon}
        title={textInputButton}
        isDisabled={isDisabled}
      />
    )
  }

  const actions = useMemo(() => {
    if (!actionItems.length) {
      return null
    }
    /* eslint-disable react/no-array-index-key */
    const mappedActions = actionItems.map((child: any, index: number) =>
      cloneElement(child, {
        key: `${id}-action-${index}`,
        tabIndex: isExpandedState ? 0 : -1,
        "aria-hidden": !isExpandedState,
        onKeyDown: (event: KeyboardEvent) => {
          if (event.which === KeyCodes.Escape) {
            setIsFocused(false)
            setIsExpanded(false)
          }
        },
      }),
    )
    /* eslint-enable react/no-array-index-key */
    return <SelectActionsWrapper>{mappedActions}</SelectActionsWrapper>
  }, [isExpandedState, actionItems])

  return (
    <InputBaseStyled
      // Accessibility
      className={className}
      onRef={(ref: MutableRefObject<HTMLDivElement | null>) => {
        refInputBase.current = ref.current
      }}
      id={id}
      role="listbox"
      tabIndex={isDisabled ? -1 : 0}
      {...getAriaProps()}
      // State props

      isFocused={isFocused}
      isLoading={isLoading}
      isDisabled={isDisabled}
      svgIcon={svgIcon}
      customIcon={customIcon}
      size={size}
      isErrorStateOverriden={isErrorStateOverriden}
      semanticState={getSemanticState()}
      onFocus={() => setIsFocused(true)}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
    >
      {Boolean(label) && (
        <FloatingLabel
          isHidden={isLabelHidden}
          id={`${id}-label`}
          isElevated={isElevated}
          isWithIcon={Boolean(svgIcon ?? customIcon)}
          isErrorStateOverriden={isErrorStateOverriden}
          htmlFor={id}
        >
          {label}
        </FloatingLabel>
      )}
      <SelectList
        label={label}
        textSubmit={textSubmit}
        isMobile={isMobile}
        isLoading={isLoading}
        isExpanded={isExpandedState}
        hasActions={Boolean(actionItems.length)}
        onRefList={(ref: MutableRefObject<HTMLDivElement | null>) => {
          refList.current = ref.current
        }}
        onSubmit={() => {
          refInputBase.current?.focus()
          setIsExpanded(false)
          setIsElevated(
            isMultiple
              ? (currentValue as Option[]).length > 0
              : currentValue !== undefined && currentValue !== null,
          )
        }}
        onEscape={handleEscape}
        onWrap={handleTab}
      >
        {renderOptionItems()}
        {actions}
      </SelectList>
      {children}
      {renderValueContainer()}
      {renderInputIcon()}
    </InputBaseStyled>
  )
}
